'use client';
import React, { useState } from 'react';
import { Bell, Search, ChevronDown, Menu, Sun, Moon, Building2 } from 'lucide-react';
import Image from 'next/image';

interface TopbarProps {
  onMobileMenuToggle?: () => void;
  pageTitle?: string;
  pageBreadcrumb?: string;
  alertCount?: number;
  theme?: string;
  onThemeChange?: (theme: string) => void;
}

export default function Topbar({ onMobileMenuToggle, pageTitle, pageBreadcrumb, alertCount = 7, theme = 'light', onThemeChange }: TopbarProps) {
  const [showThemeMenu, setShowThemeMenu] = useState(false);

  const themes = [
    { id: 'light', label: 'Clair', icon: Sun },
    { id: 'dark', label: 'Sombre', icon: Moon },
    { id: 'institutional', label: 'Institutionnel', icon: Building2 },
  ];

  const currentTheme = themes.find(t => t.id === theme) || themes[0];
  const ThemeIcon = currentTheme.icon;

  return (
    <header
      className="h-16 flex items-center justify-between px-4 lg:px-6 shrink-0 sticky top-0 z-30 border-b"
      style={{ background: '#fff', borderColor: '#e5e7eb' }}
    >
      <div className="flex items-center gap-4">
        <button
          onClick={onMobileMenuToggle}
          className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Menu mobile"
        >
          <Menu size={20} className="text-gray-500" />
        </button>
        {pageTitle && (
          <div>
            {pageBreadcrumb && (
              <p className="text-xs text-gray-400">{pageBreadcrumb}</p>
            )}
            <h1 className="text-base font-semibold" style={{ color: '#1B4F72' }}>{pageTitle}</h1>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        {/* Search */}
        <div className="hidden md:flex items-center gap-2 rounded-lg px-3 py-2 w-56" style={{ background: '#F4F6F8' }}>
          <Search size={14} className="text-gray-400 shrink-0" />
          <input
            type="text"
            placeholder="Rechercher un titre, société..."
            className="bg-transparent text-sm text-gray-700 placeholder:text-gray-400 outline-none flex-1 min-w-0"
          />
        </div>

        {/* Theme Selector */}
        <div className="relative">
          <button
            onClick={() => setShowThemeMenu(!showThemeMenu)}
            className="flex items-center gap-1.5 p-2 rounded-lg hover:bg-gray-100 transition-colors"
            title="Changer le thème"
          >
            <ThemeIcon size={16} className="text-gray-500" />
            <span className="hidden md:block text-xs text-gray-500">{currentTheme.label}</span>
          </button>
          {showThemeMenu && (
            <div className="absolute right-0 top-full mt-1 bg-white border rounded-xl shadow-lg z-50 py-1 min-w-[160px]" style={{ borderColor: '#e5e7eb' }}>
              {themes.map(t => {
                const TIcon = t.icon;
                return (
                  <button
                    key={t.id}
                    onClick={() => { onThemeChange?.(t.id); setShowThemeMenu(false); }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${theme === t.id ? 'font-semibold' : ''}`}
                    style={{ color: theme === t.id ? '#1B4F72' : '#374151' }}
                  >
                    <TIcon size={14} />
                    {t.label}
                    {theme === t.id && <span className="ml-auto text-xs" style={{ color: '#1B4F72' }}>✓</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Notifications */}
        <button className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors">
          <Bell size={18} className="text-gray-500" />
          {alertCount > 0 && (
            <span
              className="absolute top-1 right-1 w-4 h-4 text-[9px] font-bold rounded-full flex items-center justify-center text-white"
              style={{ background: '#C0392B' }}
            >
              {alertCount}
            </span>
          )}
        </button>

        {/* User with DG photo */}
        <div className="flex items-center gap-2 cursor-pointer">
          <div className="w-8 h-8 rounded-full overflow-hidden border-2 shrink-0" style={{ borderColor: '#1B4F72' }}>
            <Image
              src="/assets/images/image-1781505422790.png"
              alt="Popol Mabolia Yenga — Directeur Général CAMI"
              width={32}
              height={32}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="hidden md:block">
            <p className="text-sm font-semibold text-gray-800 leading-tight">Popol Mabolia Yenga</p>
            <p className="text-xs text-gray-400 leading-tight">Directeur Général</p>
          </div>
          <ChevronDown size={14} className="text-gray-400 hidden md:block" />
        </div>
      </div>
    </header>
  );
}